import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { DashboardFiltersFacade } from '../../domain/+state/dashboard-filters.facade';
import { DashboardFacade } from '../../domain/+state/dashboard.facade';
import { DashboardFilters } from '../../domain/model/dashboard-filters.model';
import { DashboardVmState } from '../../domain/model/dashboard-vm.model';
import { TrendMetrics, TrendMetricsDataVm, TrendMetricsVm } from '../../domain/model/trend-metrics.model';
import {
  comparisonLabel,
  ConversionSelection,
  sameConversionSelection,
  toConversionSelection,
} from '../../shared/conversion-metrics.helper';
import { companyToday, resolveDateRange } from '../../shared/dashboard-filters.helper';
import { clampToNow, errorVm, loadingVm } from '../../shared/dashboard-vm.helper';
import { toTrendChartVm, toTrendTileVms } from '../../shared/trend-metrics.helper';

const LOADING_VM: TrendMetricsVm = loadingVm<TrendMetricsDataVm>();

/** Everything the template reads is assembled here: no arithmetic and no string building in the markup. */
function toDataVm(metrics: TrendMetrics, selection: ConversionSelection): TrendMetricsDataVm {
  // From the response's own window, never the filter bar.
  const label = comparisonLabel(metrics.comparison);
  return {
    chart: toTrendChartVm(metrics),
    tiles: toTrendTileVms(metrics.tiles, selection.period, label),
  };
}

/**
 * Screen facade of the trend metrics page: one request per distinct `(from, to, scope, period)` and a precomputed
 * view model. Component-provided; lives as long as the screen.
 */
@Injectable()
export class TrendMetricsFacade {
  /** loading → data | error per request. */
  readonly vm$: Observable<TrendMetricsVm>;

  constructor(
    private readonly dashboardFacade: DashboardFacade,
    private readonly filtersFacade: DashboardFiltersFacade,
  ) {
    // The range is resolved from the same emission as the period: pairing `filters$` with `dateRange$` would
    // emit the new period with the old range first, a request the server rejects.
    this.vm$ = combineLatest([this.filtersFacade.filters$, this.filtersFacade.now$]).pipe(
      map(([filters, now]: [DashboardFilters, Date]) =>
        toConversionSelection(filters, resolveDateRange(filters, companyToday(now))),
      ),
      filter((selection): selection is ConversionSelection => selection !== null),
      distinctUntilChanged(sameConversionSelection),
      switchMap((selection: ConversionSelection) => this.load(selection)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Errors are caught here so one failed request never ends `vm$`; the next filter change fetches again. */
  private load(selection: ConversionSelection): Observable<TrendMetricsVm> {
    // Built once per response: a new chart reference per clock tick would re-animate the canvas.
    const response$ = this.dashboardFacade
      .getTrendMetrics$(selection.range, selection.scope, selection.period)
      .pipe(map((metrics: TrendMetrics) => ({ asOfUtc: metrics.asOfUtc, data: toDataVm(metrics, selection) })));
    return combineLatest([response$, this.filtersFacade.now$]).pipe(
      map(
        ([response, now]): TrendMetricsVm => ({
          state: DashboardVmState.DATA,
          asOfUtc: clampToNow(response.asOfUtc, now),
          errorMessage: null,
          data: response.data,
        }),
      ),
      startWith(LOADING_VM),
      catchError((error: unknown) => of(errorVm<TrendMetricsDataVm>(error))),
    );
  }
}
